import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, RootState } from '../../../../store';
import { useSelector } from 'react-redux';
import { showFeedbackModal } from '../../../../store/actions/UserFeedbackActions';
import { fetchUserProfile, uploadProfilePicture, deleteProfilePicture } from '../../../../store/actions/DataAction';
import { showModal, hideModal } from '../../../../store/actions/modalActions';
import { fetchPlatformLanguages, fetchPracticeAreas } from '../../../../store/actions/DropdownActions';
import apis from '../../../../utils/apis';
import Input from '../../../shared/TailwindComponents/Input';
import Button from '../../../shared/TailwindComponents/Button';
import Dropdown from '../../../shared/TailwindComponents/Dropdown';
import Badge from '../../../shared/TailwindComponents/Badge';
import {
  PiEnvelopeLight,
  PiUserLight,
  PiMoneyLight,
  PiCalendarLight,
  PiBriefcaseLight,
  PiClockLight,
  PiCheckCircleLight,
  PiWarningLight,
  PiCameraLight,
  PiTrashLight,
} from 'react-icons/pi';
import { formatDateTime, formatRole } from '../../../../utils/functions';
import ProfileImage from '../../../Header/ProfileImage';
import PersonalTabSkeleton from './PersonalTabSkeleton';
import ProfilePictureCropModal from '../ProfilePictureCropModal';
import { useFeatureTour } from '../../../../hooks/useFeatureTour';
import { FeatureTour, personalTabTourSteps } from '../../../shared/FeatureTour';

interface UserProfile {
  hourly_rate?: number;
  matters_solved?: number;
  fixed_working_hours?: number;
  license_document_link?: string;
  experience_years?: number;
  specializations?: string[];
  bio?: string;
  languages_spoken?: string[];
  education?: any[];
  certifications?: any[];
  professional_memberships?: string[];
  availability_status?: string;
  awards?: string[];
  publications?: string[];
  profile_picture?: string;
  department?: string;
  is_external?: boolean;
  join_date?: string;
  updated_at?: string;
}

interface UserInfo {
  email: string;
  first_name: string;
  last_name: string;
  id: string;
  role?: string;
}

const PersonalTab: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { user: auth0User } = useAuth0();
  const reduxUserInfo = useSelector((state: RootState) => state.user?.userInfo);
  const platformLanguages = useSelector((state: RootState) => state.dropdown.dropdowns.platformLanguages);
  const practiceAreas = useSelector((state: RootState) => state.dropdown.dropdowns.practiceAreas);

  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [userInfo, setUserInfo] = useState<UserInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editedProfile, setEditedProfile] = useState<Partial<UserProfile>>({});
  const [editedUserInfo, setEditedUserInfo] = useState<Partial<UserInfo>>({});
  const [hasChanges, setHasChanges] = useState(false);
  const [lastUpdated, setLastUpdated] = useState<string | null>(null);
  const [isUploadingPicture, setIsUploadingPicture] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { isRunning: isTourRunning, handleJoyrideCallback: handleTourCallback } = useFeatureTour({
    tourId: 'personal-profile',
    steps: personalTabTourSteps,
    autoStartDelay: 600,
    readyToShow: !loading,
  });

  const availabilityOptions = [
    { value: 'available', label: t('Available') },
    { value: 'on_leave', label: t('On Leave') },
    { value: 'unavailable', label: t('Unavailable') },
  ];

  const membershipOptions = [
    { value: 'American Bar Association', label: 'American Bar Association' },
    { value: 'State Bar Association', label: 'State Bar Association' },
    { value: 'International Bar Association', label: 'International Bar Association' },
  ];

  useEffect(() => {
    if (!platformLanguages || platformLanguages.length === 0) {
      dispatch(fetchPlatformLanguages() as any);
    }
    if (!practiceAreas || practiceAreas.length === 0) {
      dispatch(fetchPracticeAreas() as any);
    }
  }, [dispatch]);

  useEffect(() => {
    // Fetch profile when component mounts or when reduxUserInfo is available
    if (auth0User) {
      fetchProfile();
    }
  }, [auth0User?.sub]);

  const fetchProfile = async () => {
    setLoading(true);
    try {
      const response = await apis.get('/user-profile/me');
      if (response.data?.success && response.data?.profile && response.data?.user_info) {
        const profileData = response.data.profile;
        const userInfoData = response.data.user_info;

        setProfile(profileData);
        setEditedProfile(profileData);
        if (profileData.updated_at) {
          setLastUpdated(profileData.updated_at);
        }

        const userInfoData_full = {
          id: userInfoData.id || '',
          email: userInfoData.email || '',
          first_name: userInfoData.first_name || '',
          last_name: userInfoData.last_name || '',
          role: userInfoData.role || '',
        };
        setUserInfo(userInfoData_full);
        setEditedUserInfo({
          first_name: userInfoData_full.first_name,
          last_name: userInfoData_full.last_name,
        });
      } else {
        throw new Error('Failed to fetch profile');
      }
    } catch (error: any) {
      console.error('Error fetching profile:', error);
      dispatch(
        showFeedbackModal({
          showModal: true,
          message: t('Failed to load profile'),
          modalType: 'error',
          duration: 5000,
        })
      );
    } finally {
      setLoading(false);
    }
  };

  const handleFieldChange = (field: keyof UserProfile, value: any) => {
    setEditedProfile((prev) => ({
      ...prev,
      [field]: value,
    }));
    setHasChanges(true);
  };

  const handleUserInfoChange = (field: keyof UserInfo, value: any) => {
    setEditedUserInfo((prev) => ({
      ...prev,
      [field]: value,
    }));
    setHasChanges(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // Only send fields that actually changed to avoid validation errors
      // on untouched fields (e.g. profile_picture is a relative path, not a URL)
      const payload: Record<string, any> = {};

      if (profile) {
        const profileKeys: (keyof UserProfile)[] = [
          'hourly_rate', 'matters_solved', 'fixed_working_hours', 'license_document_link',
          'experience_years', 'specializations', 'bio', 'languages_spoken', 'education',
          'certifications', 'professional_memberships', 'availability_status', 'awards',
          'publications', 'department', 'is_external',
        ];
        for (const key of profileKeys) {
          const oldVal = profile[key];
          const newVal = editedProfile[key];
          if (JSON.stringify(oldVal) !== JSON.stringify(newVal)) {
            payload[key] = newVal;
          }
        }
      }

      if (editedUserInfo.first_name !== userInfo?.first_name) {
        payload.first_name = editedUserInfo.first_name;
      }
      if (editedUserInfo.last_name !== userInfo?.last_name) {
        payload.last_name = editedUserInfo.last_name;
      }

      const response = await apis.put('/user-profile/me', payload);

      if (response.data?.success) {
        // Preserve current profile_picture — the save response returns the blob
        // storage name (e.g. "blob:profile_picture.webp"), not the serving URL.
        // Profile picture is managed separately via upload/delete endpoints.
        const updatedProfile = {
          ...response.data.profile,
          profile_picture: profile?.profile_picture,
        };
        if (response.data.user_info?.profile_picture) {
          updatedProfile.profile_picture = response.data.user_info.profile_picture;
        }
        setProfile(updatedProfile);
        setEditedProfile(updatedProfile);

        // Update userInfo if returned in response
        if (response.data.user_info) {
          const updatedUserInfo = {
            id: response.data.user_info.id || '',
            email: response.data.user_info.email || '',
            first_name: response.data.user_info.first_name || '',
            last_name: response.data.user_info.last_name || '',
            role: response.data.user_info.role || '',
          };
          setUserInfo(updatedUserInfo);
          setEditedUserInfo({
            first_name: updatedUserInfo.first_name,
            last_name: updatedUserInfo.last_name,
          });
        }

        setHasChanges(false);
        if (response.data.profile.updated_at) {
          setLastUpdated(response.data.profile.updated_at);
        }

        // Refetch user profile to update Redux state (updates Sidebar, Header, etc.)
        dispatch(fetchUserProfile() as any);

        dispatch(
          showFeedbackModal({
            showModal: true,
            message: t('Profile updated successfully'),
            modalType: 'success',
            duration: 3000,
          })
        );
      } else {
        throw new Error('Failed to update profile');
      }
    } catch (error: any) {
      console.error('Error saving profile:', error);
      // dispatch(
      //   showFeedbackModal({
      //     showModal: true,
      //     message: error.response?.data?.message || t('Failed to update profile'),
      //     modalType: 'error',
      //     duration: 5000,
      //   })
      // );
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setEditedProfile(profile || {});
    setEditedUserInfo({
      first_name: userInfo?.first_name || '',
      last_name: userInfo?.last_name || '',
    });
    setHasChanges(false);
  };

  const handleProfilePictureSelect = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    // Reset input so the same file can be re-selected
    if (fileInputRef.current) fileInputRef.current.value = '';

    // Validate on the client: max 5MB, image types only
    if (file.size > 5 * 1024 * 1024) {
      dispatch(showFeedbackModal({
        showModal: true,
        message: t('Image must be smaller than 5MB'),
        modalType: 'error',
        duration: 4000,
      }));
      return;
    }
    if (!file.type.startsWith('image/')) {
      dispatch(showFeedbackModal({
        showModal: true,
        message: t('Please select an image file'),
        modalType: 'error',
        duration: 4000,
      }));
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = reader.result as string;
      dispatch(showModal({
        type: 'custom',
        message: t('Edit Profile Picture'),
        width: '480px',
        content: (
          <ProfilePictureCropModal
            imageSrc={dataUrl}
            onCropComplete={async (croppedBlob) => {
              dispatch(hideModal());
              setIsUploadingPicture(true);
              try {
                await dispatch(uploadProfilePicture(croppedBlob));
                // Refresh local profile data
                await fetchProfile();
              } finally {
                setIsUploadingPicture(false);
              }
            }}
            onClose={() => {
              dispatch(hideModal());
            }}
        />
      ),
      showModal: true,
    }));
    };
    reader.readAsDataURL(file);
  };

  const handleRemoveProfilePicture = async () => {
    setIsUploadingPicture(true);
    try {
      await dispatch(deleteProfilePicture());
      await fetchProfile();
    } finally {
      setIsUploadingPicture(false);
    }
  };

  if (loading) {
    return <PersonalTabSkeleton />;
  }

  if (!profile || !userInfo) {
    return (
      <div className="text-center py-12">
        <PiWarningLight className="h-12 w-12 text-yellow-600 mx-auto mb-4" />
        <p className="text-gray-600 dark:text-gray-400">{t('Unable to load profile')}</p>
      </div>
    );
  }

  const profilePicture = editedProfile.profile_picture || profile.profile_picture;
  const displayName = `${userInfo.first_name} ${userInfo.last_name}`.trim();
  const initials = `${userInfo.first_name?.[0] || ''}${userInfo.last_name?.[0] || ''}`.toUpperCase();

  return (
    <>
    <div className="flex flex-col h-full w-full bg-transparent dark:bg-transparent px-4 pt-4">
      {/* Scrollable Content Area */}
      <div className="flex-1 overflow-y-auto overflow-x-hidden custom-scrollbar-main pb-6">
        {/* Header */}
        <div data-tour-id="personal-header" className="pt-0 pb-4 border-b border-gray-200 dark:border-gray-700 flex justify-between items-start mb-4">
          <div className="flex gap-4 items-start">
            <div className="flex-shrink-0 relative group">
              <ProfileImage
                profilePicture={profilePicture || null}
                email={userInfo.email}
                initials={initials}
                name={displayName}
                size="w-16 h-16"
              />
              {/* Hover overlay for upload */}
              <button
                type="button"
                onClick={handleProfilePictureSelect}
                disabled={isUploadingPicture}
                className="absolute inset-0 rounded-full bg-black/0 group-hover:bg-black/40 flex items-center justify-center transition-all duration-200 cursor-pointer"
                aria-label={t('Change profile picture')}
              >
                <PiCameraLight className="w-5 h-5 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-200" />
              </button>
              {isUploadingPicture && (
                <div className="absolute inset-0 rounded-full bg-black/30 flex items-center justify-center">
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                </div>
              )}
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp,image/gif"
                className="hidden"
                onChange={handleFileChange}
              />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">{displayName}</h2>
              <p className="text-sm text-gray-600 dark:text-gray-400">{userInfo.email}</p>
              {userInfo.role && (
                <p className="mt-1 text-xs text-gray-600 dark:text-gray-400">
                  {formatRole(userInfo.role)}
                </p>
              )}
              {profilePicture && (
                <button
                  type="button"
                  onClick={handleRemoveProfilePicture}
                  disabled={isUploadingPicture}
                  className="mt-1 text-xs text-red-500 hover:text-red-600 flex items-center gap-1 transition-colors"
                >
                  <PiTrashLight className="w-3 h-3" />
                  {t('Remove photo')}
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Two Column Layout */}
        <div data-tour-id="personal-form" className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Left Column - Personal Information */}
          <div className="space-y-4">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 border-b border-gray-200 dark:border-gray-700 pb-3">
                {t('Personal Information')}
              </h3>
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <Input
                    label={t('First Name')}
                    type="text"
                    value={editedUserInfo.first_name || ''}
                    onChange={(e) => handleUserInfoChange('first_name', e.target.value)}
                    placeholder={t('John')}
                    icon={<PiUserLight />}
                  />
                  <Input
                    label={t('Last Name')}
                    type="text"
                    value={editedUserInfo.last_name || ''}
                    onChange={(e) => handleUserInfoChange('last_name', e.target.value)}
                    placeholder={t('Doe')}
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <Input
                    label={t('Hourly Rate ($)')}
                    type="number"
                    value={editedProfile.hourly_rate || ''}
                    onChange={(e) =>
                      handleFieldChange('hourly_rate', e.target.value ? parseInt(e.target.value) : undefined)
                    }
                    placeholder={t('e.g., 250')}
                    icon={<PiMoneyLight />}
                  />
                  <Input
                    label={t('Years of Experience')}
                    type="number"
                    value={editedProfile.experience_years || ''}
                    onChange={(e) =>
                      handleFieldChange('experience_years', e.target.value ? parseInt(e.target.value) : undefined)
                    }
                    placeholder={t('e.g., 8')}
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <Input
                    label={t('Fixed Working Hours/Week')}
                    type="number"
                    value={editedProfile.fixed_working_hours || ''}
                    onChange={(e) =>
                      handleFieldChange('fixed_working_hours', e.target.value ? parseInt(e.target.value) : undefined)
                    }
                    placeholder={t('e.g., 40')}
                    icon={<PiClockLight />}
                  />
                  <Dropdown
                    label={t('Availability Status')}
                    options={availabilityOptions}
                    value={editedProfile.availability_status || 'available'}
                    onChange={(val) => handleFieldChange('availability_status', val)}
                    fullWidth={true}
                  />
                </div>

                <Dropdown
                  label={t('Languages Spoken')}
                  options={platformLanguages}
                  multiple
                  value={editedProfile.languages_spoken || []}
                  onChange={(val) => handleFieldChange('languages_spoken', Array.isArray(val) ? val : [val])}
                  placeholder={t('Select languages you speak')}
                  fullWidth={true}
                />
              </div>
            </div>
          </div>

          {/* Right Column - Professional Summary */}
          <div className="space-y-4">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 border-b border-gray-200 dark:border-gray-700 pb-3">
                {t('Professional Summary')}
              </h3>
              <div className="space-y-4">
                <div className="space-y-2">
                  <label className="block text-xs font-normal text-gray-700 dark:text-gray-300">
                    {t('Bio')}
                  </label>
                  <textarea
                    value={editedProfile.bio || ''}
                    onChange={(e) => handleFieldChange('bio', e.target.value)}
                    placeholder={t('Tell us about your professional background and expertise...')}
                    className="w-full h-32 px-3 py-2 bg-white dark:bg-gray-900 rounded-md shadow-sm placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-primary-500 dark:focus:ring-primary-700 transition duration-300 text-xs border border-gray-300 dark:border-gray-600 resize-none"
                  />
                </div>

                <Dropdown
                  label={t('Specializations')}
                  options={practiceAreas || []}
                  multiple
                  value={editedProfile.specializations || []}
                  onChange={(val) => handleFieldChange('specializations', Array.isArray(val) ? val : [val])}
                  placeholder={t('Select your areas of practice')}
                  fullWidth={true}
                />
                {/* <Dropdown
                  label={t('Professional Memberships')}
                  options={membershipOptions}
                  multiple
                  value={editedProfile.professional_memberships || []}
                  onChange={(val) => handleFieldChange('professional_memberships', Array.isArray(val) ? val : [val])}
                  placeholder={t('Select bar associations and memberships')}
                  fullWidth={true}
                /> */}

                {lastUpdated && (
                  <div className="flex items-center gap-2 p-3 bg-gray-50 dark:bg-gray-800 rounded-lg mt-6">
                    <PiCheckCircleLight className="text-green-600 h-5 w-5 flex-shrink-0" />
                    <p className="text-xs text-gray-600 dark:text-gray-400">
                      {t('Last updated')}: {formatDateTime(lastUpdated)}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Footer - Fixed at bottom of viewport */}
      <div className="border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 py-4 px-4 flex items-center justify-end gap-3 flex-shrink-0 -mx-4">
        <Button
          variant="outline"
          size="small"
          onClick={handleCancel}
          disabled={!hasChanges || saving}
        >
          {t('Cancel')}
        </Button>
        <Button
          variant="primary"
          size="small"
          onClick={handleSave}
          disabled={!hasChanges || saving}
          isLoading={saving}
        >
          {t(saving ? 'Saving...' : 'Save Changes')}
        </Button>
      </div>
    </div>
    <FeatureTour
      steps={personalTabTourSteps}
      run={isTourRunning}
      onCallback={handleTourCallback}
      disableOverlay
    />
    </>
  );
};

export default PersonalTab;
